import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Modal from './Modal';
import {
  Lock,
  AlertTriangle,
  Eye,
  EyeOff,
  Shield
} from 'lucide-react';

const ModalAutorizacao = ({ isOpen, onClose, onConfirm, loading, liberacao }) => {
  const { logout } = useAuth();
  const [senha, setSenha] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [tentativas, setTentativas] = useState(0);
  const [message, setMessage] = useState({ type: '', text: '' });
  const maxTentativas = 3;

  useEffect(() => {
    if (isOpen) {
      // Reset quando o modal abrir
      setSenha('');
      setTentativas(0);
      setMessage({ type: '', text: '' });
      setShowPassword(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!senha.trim()) {
      setMessage({ type: 'error', text: 'Digite sua senha' });
      return;
    }

    try {
      await onConfirm(senha);
      // Se chegou até aqui, a autorização foi bem-sucedida
      onClose();
    } catch (error) {
      const novasTentativas = tentativas + 1;
      setTentativas(novasTentativas);
      
      if (error.response?.status === 401) {
        const tentativasRestantes = maxTentativas - novasTentativas;
        
        if (novasTentativas >= maxTentativas) {
          // Máximo de tentativas atingido - deslogar usuário
          setMessage({ 
            type: 'error', 
            text: 'Máximo de tentativas excedido. Você será desconectado por segurança.' 
          });
          
          setTimeout(() => {
            logout();
          }, 2000);
        } else {
          setMessage({ 
            type: 'error', 
            text: `Senha incorreta. Você tem mais ${tentativasRestantes} tentativa${tentativasRestantes !== 1 ? 's' : ''}.` 
          });
        }
      } else {
        setMessage({ 
          type: 'error', 
          text: error.response?.data?.message || 'Erro ao autorizar liberação' 
        });
      }
      
      setSenha('');
    }
  };

  const handleClose = () => {
    if (!loading && tentativas < maxTentativas) {
      onClose();
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose}
      title="Autorização Necessária"
      size="medium"
    >
      {/* Header com informações da liberação */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #dee2e6'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <Shield size={20} style={{ color: '#007bff' }} />
          <h4 style={{ margin: 0, color: '#333' }}>Confirmação de Liberação</h4>
        </div>
        
        {liberacao && (
          <div style={{ fontSize: '14px', color: '#667' }}>
            <p style={{ margin: '5px 0' }}>
              <strong>Recibo:</strong> {liberacao.numeroRecibo}
            </p>
            <p style={{ margin: '5px 0' }}>
              <strong>Paciente:</strong> {liberacao.paciente?.nome}
            </p>
            <p style={{ margin: '5px 0' }}>
              <strong>Total de Itens:</strong> {liberacao.totalItens}
            </p>
          </div>
        )}
      </div>

      {/* Mensagem de aviso */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '15px',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '6px',
        marginBottom: '20px'
      }}>
        <AlertTriangle size={20} style={{ color: '#856404', marginTop: '2px' }} />
        <div style={{ fontSize: '14px', color: '#856404' }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
            Confirmação de segurança necessária
          </p>
          <p style={{ margin: 0 }}>
            Para autorizar esta liberação de medicamentos, confirme sua identidade digitando sua <strong>senha de liberação</strong>.
          </p>
          {tentativas > 0 && (
            <p style={{ margin: '8px 0 0 0', fontWeight: 'bold' }}>
              ⚠️ Tentativa {tentativas} de {maxTentativas}
            </p>
          )}
        </div>
      </div>

      {/* Mensagem de erro/sucesso */}
      {message.text && (
        <div style={{
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '20px',
          backgroundColor: message.type === 'error' ? '#f8d7da' : '#d4edda',
          border: `1px solid ${message.type === 'error' ? '#f5c6cb' : '#c3e6cb'}`,
          color: message.type === 'error' ? '#721c24' : '#155724'
        }}>
          {message.text}
        </div>
      )}

      {/* Formulário */}
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '20px' }}>
          <label className="form-label" style={{ fontWeight: 'bold' }}>
            <Lock size={16} style={{ marginRight: '5px' }} />
            Digite sua senha de liberação
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              className="form-control"
              placeholder="Sua senha de liberação"
              required
              disabled={loading || tentativas >= maxTentativas}
              autoFocus
              style={{
                paddingRight: '45px',
                fontSize: '16px',
                ...(tentativas >= maxTentativas && {
                  backgroundColor: '#f8f9fa',
                  cursor: 'not-allowed'
                })
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading || tentativas >= maxTentativas}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                border: 'none',
                background: 'none',
                cursor: tentativas >= maxTentativas ? 'not-allowed' : 'pointer',
                color: '#667',
                padding: '5px'
              }}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* Botões de ação */}
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          justifyContent: 'flex-end',
          paddingTop: '20px',
          borderTop: '1px solid #dee2e6'
        }}>
          <button
            type="button"
            onClick={handleClose}
            className="btn btn-secondary"
            disabled={loading || tentativas >= maxTentativas}
          >
            Cancelar
          </button>
          
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !senha.trim() || tentativas >= maxTentativas}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {loading ? (
              <>
                <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
                Autorizando...
              </>
            ) : tentativas >= maxTentativas ? (
              'Bloqueado'
            ) : (
              <>
                <Shield size={16} />
                Autorizar Liberação
              </>
            )}
          </button>
        </div>
      </form>

      {/* Informações de segurança */}
      <div style={{
        marginTop: '20px',
        padding: '12px',
        backgroundColor: '#e9ecef',
        borderRadius: '6px',
        fontSize: '12px',
        color: '#6c757d'
      }}>
        <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>Política de Segurança:</p>
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          <li>Máximo de {maxTentativas} tentativas de senha</li>
          <li>Após {maxTentativas} tentativas incorretas, você será desconectado</li>
          <li>Esta ação será registrada no sistema</li>
        </ul>
      </div>
    </Modal>
  );
};

export default ModalAutorizacao;